import css from '@styled-system/css';
import styled from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Copy, H6, Markdown, MarkdownHeading, Optional } from '@honda-canada/design-system-react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { useTranslation } from 'react-i18next';

import getIOSVersion from '../../utils/getIOSVersion';
import gtmReadMoreClick from '../../core/gtm/gtmReadMoreClick';
import themeStyles from './FreeFormText.styles';
import useConfiguration from './service/useConfiguration';
import { mapGTMCategory } from '../../utils/sitecoreFields';
import { parseBool } from '../../utils/common';
import {
  selectBodyTypesForGtm,
  selectCurrentVehicleFromVehicles,
  selectModelNameByModelKey,
} from '../../core/reducers/vehicles';
import { selectCurrentVehicle } from '../../core/reducers/currentVehicle';
import { useAppSelector } from '../../core/utils';

const StyledClamps = themeStyles.apply(Box, 'StyledClamps');
const StyledBoxWrapper = themeStyles.apply(Box, 'StyledBoxWrapper');
const ReadMoreLess = themeStyles.apply(Copy, 'ReadMoreLess');

const StyledMarkdown = styled(Markdown)(
  css({
    // Overrides some opinionated CSS from the Acura current-state header
    sup: {
      lineHeight: 'inherit',
    },
  }),
);

const LINE_HEIGHT_ADJUSTMENT_FACTOR = 0.88;

const FreeFormTextUI = ({ fields, params, rendering, variant }) => {
  const trimNameFromStore = useAppSelector(selectCurrentVehicleFromVehicles)?.trimName;
  const modelName = useAppSelector(selectModelNameByModelKey)?.toLowerCase();
  const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
  const { inventoryStatus } = useAppSelector(selectCurrentVehicle);

  const { isUnderlinedReadMore } = useConfiguration(variant);
  const { t } = useTranslation();
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [maxHeightValue, setMaxHeightValue] = useState(null);

  const linesToShow = fields?.linesToShow?.value;
  const isLegal = fields?.isLegal?.value;
  const [showReadMore, setShowReadMore] = useState(true);

  const readMoreLabel = showReadMore ? t('Shared.Common.readMoreLabel') : t('Shared.Common.readLessLabel');
  const { trimKey } = useAppSelector(selectCurrentVehicle) || {};
  const trimName = trimNameFromStore?.toLowerCase() || trimKey?.replace(/_/g, ' ')?.replace(/\d+$/, '')?.trim();

  useEffect(() => {
    if (textRef.current) {
      const iOSVersion = getIOSVersion();
      const textContent = textRef.current.innerText || '';
      const emptyLinesCount = textContent.split('\n').filter(line => line.trim() === '').length;
      const currentHeight = parseInt(window.getComputedStyle(textRef.current).height, 10);
      const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight, 10);
      const actualLines = Math.floor(currentHeight / lineHeight);
      setIsOverflowing(actualLines > emptyLinesCount + +linesToShow);
      if (iOSVersion >= 16 && iOSVersion < 17) {
        setMaxHeightValue(+linesToShow * lineHeight * LINE_HEIGHT_ADJUSTMENT_FACTOR);
      }
    }
  }, []);

  const readMoreHandler = () => {
    setShowReadMore(!showReadMore);
    gtmReadMoreClick(rendering?.componentName, modelName, trimName, gtmBodyStyle, inventoryStatus);
  };

  if (!fields || !fields.contentBody || !fields.contentBody.value) {
    return null;
  }

  const hasReadMore = !!linesToShow && isOverflowing;

  const marginBottom = rendering?.params?.bottomMargin ? `${rendering?.params?.bottomMargin}px` : ['xl', 'xxl'];
  const marginTop = rendering?.params?.topMargin ? `${rendering?.params?.topMargin}px` : 'zero';

  return (
    <StyledBoxWrapper
      id={fields.anchorId?.value}
      textAlign={params?.contentAlignment?.toLowerCase()}
      width={parseBool(params?.isReducedWidth) ? [1, 3 / 4] : 'initial'}
      mb={hasReadMore ? undefined : marginBottom}
      mt={hasReadMore ? undefined : marginTop}
      data-gtm-component-type={rendering?.componentName}
      data-gtm-category={mapGTMCategory(fields.gtmCategory)}
    >
      {fields.contentTitle?.value && (
        <Box mb={['default', 'l']}>
          <MarkdownHeading headingOverride="h3">{fields.contentTitle?.value}</MarkdownHeading>
        </Box>
      )}
      <Box pb={!showReadMore && 'default'}>
        {fields.subtitle?.value && (
          <H6 as={fields.seoH1?.value === 'Subtitle' && 'h1'}>
            <Text field={fields.subtitle} />
          </H6>
        )}
        <StyledClamps
          ref={textRef}
          data-testid="clamped-text"
          linesToShow={linesToShow}
          showReadMore={showReadMore}
          isLegal={isLegal}
          isOverflowing={isOverflowing}
          maxHeightValue={maxHeightValue}
        >
          <StyledMarkdown
            options={
              fields.seoH1?.value === 'Description' && {
                overrides: {
                  p: {
                    component: ({ children, type, ...props }) => {
                      const Elem = type;
                      return <Elem {...props}>{children}</Elem>;
                    },
                    props: {
                      className: 'override',
                      type: 'h1',
                    },
                  },
                },
              }
            }
          >
            {fields.contentBody.value}
          </StyledMarkdown>
        </StyledClamps>
        <Optional when={hasReadMore}>
          <ReadMoreLess
            textAlign="left"
            role="button"
            onClick={readMoreHandler}
            isLegal={isLegal}
            isUnderlinedReadMore={isUnderlinedReadMore}
          >
            {readMoreLabel}
          </ReadMoreLess>
        </Optional>
      </Box>
    </StyledBoxWrapper>
  );
};

export default FreeFormTextUI;
